// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { navigationDrawerStyles } from "@ces/Overrides/Components/NavigationDrawer";
import { constants } from "@ces/XDL/constants";
import Logout from "@ces/XDL/Icons/Logout";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, mergeWithCss, styledScrollbar } from "@ces/XDL/Utilities";
import { selectedPanelItem, sharedPanelMenuStyles } from "@ces/XDL/Utilities/sharedStyles";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

navigationDrawerStyles.menuTriggerButton = {
    size: 18,
    variant: "tertiary",
    css: css`
        &,
        &:hover,
        &:focus {
            padding: 6px;
            border: none;
            background-color: transparent;
        }
        ${IconWrapper} {
            color: ${color("text.link")};
        }
    `,
};

navigationDrawerStyles.drawer = {
    mergeCss: true,
    cssOverrides: {
        drawerBody: css`
            top: ${constants.header.tablet.size.height + 1}px;
            transform: translate(-100%, 0);
            height: calc(100% - ${constants.header.tablet.size.height + 1}px);
            box-shadow: none;
            padding: 10px 0 10px 8px;
            background-color: ${color("common.white")};
            overflow: hidden;
        `,
        drawerTitle: css`
            display: none;
        `,
        scrim: css`
            background-color: ${color("common.black", 0.33)};
            top: ${constants.header.tablet.size.height + 1}px;
            height: calc(100% - ${constants.header.tablet.size.height + 1}px);
        `,
    },
};

navigationDrawerStyles.drawerSectionWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        row-gap: 4px;
        ${styledScrollbar()}
    `,
};

navigationDrawerStyles.mainNavigationRow = {
    css: css`
        margin: 0;
        padding: 6px 8px;
        border-radius: 2px;
        min-height: 32px;
        ${IconWrapper} {
            color: ${color("text.link")};
            height: 22px;
            width: 5px;
        }
        &:hover {
            ${selectedPanelItem}
        }
    `,
    isCurrentCss: selectedPanelItem,
};

navigationDrawerStyles.mainNavigationRowTypography = {
    transform: "capitalize",
    color: "common.black",
    weight: fontConfig.weight.bold,
    size: 14,
    lineHeight: "22px",
};

navigationDrawerStyles.panelMenu = {
    ...sharedPanelMenuStyles,
    cssOverrides: {
        ...sharedPanelMenuStyles.cssOverrides,
        wrapper: css`
            ${sharedPanelMenuStyles.cssOverrides?.wrapper}
            margin-right: 8px;
            & > ${IconWrapper} {
                margin-right: 0;
            }
        `,
    },
    parentLinkButton: mergeWithCss(sharedPanelMenuStyles.parentLinkButton, {
        css: css`
            margin-bottom: 6px;
        `,
    }),
};

navigationDrawerStyles.drawerSectionSeparator = {
    css: css`
        margin: 16px auto;
        border-top: 1px solid ${color("common.ui4")};
        width: calc(100% - 16px);
    `,
};

navigationDrawerStyles.accountPanelSectionWrapper = {
    css: css`
        display: flex;
        align-items: center;
        width: 100%;
    `,
};

navigationDrawerStyles.addressWrapper = {
    changeLink: {
        sizeVariant: "large",
        css: css`
            border: none;
            padding: 8px;
            min-height: auto;
        `,
        typographyProps: {
            lineHeight: "22px",
            color: "secondary",
        },
    },
};

navigationDrawerStyles.headerShipAddress = {
    shipToAddressWrapper: {
        css: css`
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            padding: 8px;
        `,
    },
    titleTypography: {
        size: 14,
        lineHeight: "22px",
        color: "text",
        ellipsis: false,
        css: css`
            white-space: pre-wrap;
            text-align: left;
        `,
    },
    titleIcon: {
        css: css`
            &&& {
                width: 14px;
                height: 14px;
                color: ${color("text.link")};
            }
            opacity: 0.66;
        `,
    },
};

navigationDrawerStyles.spotContainer = {
    css: css`
        width: 100%;
        padding: 8px;
        ${TypographyStyle} {
            font-size: 14px;
            line-height: 22px;
        }
    `,
};

navigationDrawerStyles.signOutRowLink = {
    icon: {
        iconProps: { src: Logout },
    },
};

navigationDrawerStyles.signOutRow = {
    css: css`
        padding-top: 0;
    `,
};
